/**
 * Access rights for projects, collections, and map entities.
 *
 * This is the only place that decides what a signed-in person may do.
 * Screens ask this file, then show or hide the control.
 *
 * WEEKEND TESTING
 * ENFORCE_ACCESS_RIGHTS is off on purpose. Signed-in people see every
 * access-gated control so they can click it and read the backend's error.
 * Public view-only links stay read-only.
 * After testing, set ENFORCE_ACCESS_RIGHTS to true. That turns these rules back on.
 */
import { ROLE_TYPES } from "./accessMapConstants";
import { canUserEdit, canUserView, getUserRole } from "./accessMapUtils";
import { isPublicMapEditorReadOnly } from "../features/share-links/utils/shareAccessRouting";

export const ENFORCE_ACCESS_RIGHTS = false;

const ASSIGNED_ROLES = new Set([
  ROLE_TYPES.OWNER,
  ROLE_TYPES.CONTRIBUTOR,
  ROLE_TYPES.VIEWER,
]);

/**
 * Backend action list. These match AccessControlService.
 *
 * Owner: view, edit, rate, change status, archive, delete, change who has access.
 * Contributor: view, edit, rate.
 * Viewer: view, rate.
 *
 * The backend's calculated role (effectiveRole.role) is used when the record
 * includes it. That already covers the creator and a role inherited from a
 * parent. When it is missing, the role on this item's access list is used.
 * A person with no personal role can still edit when public access is Edit,
 * and can view when public access is View or Edit.
 */

/**
 * Frontend-only rules. The backend does not enforce these. They stay here so
 * they can be read and changed in this one file.
 *
 * - Contributors and viewers do not see archived projects, collections, or entities.
 * - Content stays locked while an item is archived. Changing status is what unlocks it.
 * - Only an owner can set or clear a collection's default map view.
 * - Map entity status changes, entity delete, and archived-entity visibility
 *   follow the collection owner, not a separate role on each entity.
 */

const allowWhileTesting = (allowed) => {
  if (!ENFORCE_ACCESS_RIGHTS) return true;
  return Boolean(allowed);
};

const normalizeRole = (value) => {
  if (!value) return null;
  const raw = typeof value === "string" ? value : value.role;
  const role = String(raw || "").trim().toUpperCase();
  if (!role) return null;
  return role;
};

const readEffectiveRole = (record) => normalizeRole(record?.effectiveRole);

/**
 * Owner, contributor, or viewer for this record.
 * Returns null when the person has no personal role.
 */
export const getAssignedRole = (record, username, currentUser = null) => {
  const effectiveRole = readEffectiveRole(record);
  if (ASSIGNED_ROLES.has(effectiveRole)) return effectiveRole;
  if (effectiveRole === ROLE_TYPES.NONE) return null;
  return getUserRole(record?.accessMap, username, currentUser);
};

const isOwnerRole = (record, username, currentUser) => {
  const effectiveRole = readEffectiveRole(record);
  if (effectiveRole === ROLE_TYPES.OWNER) return true;
  if (
    effectiveRole === ROLE_TYPES.CONTRIBUTOR ||
    effectiveRole === ROLE_TYPES.VIEWER ||
    effectiveRole === ROLE_TYPES.NONE
  ) {
    return false;
  }
  return getUserRole(record?.accessMap, username, currentUser) === ROLE_TYPES.OWNER;
};

const roleAllowsEdit = (record, username, currentUser) => {
  const effectiveRole = readEffectiveRole(record);
  if (effectiveRole === ROLE_TYPES.OWNER || effectiveRole === ROLE_TYPES.CONTRIBUTOR) {
    return true;
  }
  if (effectiveRole === ROLE_TYPES.VIEWER || effectiveRole === ROLE_TYPES.NONE) {
    return false;
  }
  return canUserEdit(record?.accessMap, username, currentUser);
};

const roleAllowsView = (record, username, currentUser) => {
  const effectiveRole = readEffectiveRole(record);
  if (ASSIGNED_ROLES.has(effectiveRole)) return true;
  if (effectiveRole === ROLE_TYPES.NONE) return false;
  return canUserView(record?.accessMap, username);
};

export const isArchivedStatus = (status) =>
  String(status || "").toUpperCase() === "ARCHIVED";

/** Content edit is off while archived. See the frontend-only rules above. */
export const isArchivedContentLocked = (record) => {
  if (!ENFORCE_ACCESS_RIGHTS) return false;
  return isArchivedStatus(record?.status);
};

export const canViewRecord = (record, username, currentUser = null) =>
  allowWhileTesting(roleAllowsView(record, username, currentUser));

export const canEditRecordContent = (record, username, currentUser = null) => {
  if (!ENFORCE_ACCESS_RIGHTS) return true;
  if (isArchivedStatus(record?.status)) return false;
  return roleAllowsEdit(record, username, currentUser);
};

export const canRateRecord = (record, username, currentUser = null) =>
  allowWhileTesting(roleAllowsView(record, username, currentUser));

/** Status changes, including archive and leaving archive, are owner-only. */
export const canChangeRecordStatus = (record, username, currentUser = null) =>
  allowWhileTesting(isOwnerRole(record, username, currentUser));

/** Delete and archive actions are owner-only. */
export const canDeleteRecord = (record, username, currentUser = null) =>
  allowWhileTesting(isOwnerRole(record, username, currentUser));

/** Team members and public access are owner-only. */
export const canManageRecordAccess = (record, username, currentUser = null) =>
  allowWhileTesting(isOwnerRole(record, username, currentUser));

/** The status dropdown in the edit window is owner-only. */
export const canEditStatusField = (record, username, currentUser = null) =>
  canChangeRecordStatus(record, username, currentUser);

export const canSeeArchivedRecord = (record, username, currentUser = null) => {
  if (!isArchivedStatus(record?.status)) return true;
  return allowWhileTesting(isOwnerRole(record, username, currentUser));
};

export const filterOutArchivedForNonOwners = (items, username, currentUser = null) =>
  (Array.isArray(items) ? items : []).filter((item) =>
    canSeeArchivedRecord(item, username, currentUser)
  );

export const canSelectRecordForBulk = (record, username, currentUser = null) =>
  canEditRecordContent(record, username, currentUser) ||
  canChangeRecordStatus(record, username, currentUser);

/** Only an owner sets the collection default view. Frontend-only rule. */
export const canSetCollectionDefaultView = (collection, username, currentUser = null) =>
  allowWhileTesting(isOwnerRole(collection, username, currentUser));

/**
 * Entity status, entity delete, and showing archived entities.
 * Frontend-only rule: these follow the collection owner.
 */
export const canManageCollectionEntities = (collection, username, currentUser = null) =>
  allowWhileTesting(isOwnerRole(collection, username, currentUser));

/** Owner and contributor can choose auto-save or manual save. Viewers cannot. */
export const canChooseEntitySaveMode = (collection, username, currentUser = null) =>
  allowWhileTesting(roleAllowsEdit(collection, username, currentUser));

/**
 * Map editor writes. Public view-only links stay locked even while
 * ENFORCE_ACCESS_RIGHTS is off.
 */
export function isMapEditorWriteDisabled({
  pageMode = "protected",
  publicAccessState = null,
  collection = null,
  collectionAccessMap = null,
  username = null,
  currentUser = null,
}) {
  if (isPublicMapEditorReadOnly(pageMode, publicAccessState)) {
    return true;
  }

  if (!ENFORCE_ACCESS_RIGHTS) {
    return false;
  }

  if (pageMode === "public") {
    return false;
  }

  const record = {
    ...(collection || {}),
    accessMap: collection?.accessMap || collectionAccessMap || null,
  };

  if (!record.accessMap || !username) {
    return true;
  }

  return !canEditRecordContent(record, username, currentUser);
}
